import {
  Injectable,
  signal,
  InjectionToken,
  inject,
  DestroyRef,
} from '@angular/core';

/** Severity of a distributed log event. */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal';

/**
 * Why a flush was triggered. `signal`-suffixed reasons originate from a
 * graceful teardown interception and are shipped synchronously (beacon).
 */
export type FlushReason =
  | 'idle'
  | 'manual'
  | 'visibility-hidden'
  | 'pagehide'
  | 'beforeunload';

/** Reasons that fire during page teardown — only these use the sync beacon path. */
const SIGNAL_FLUSH_REASONS = new Set<FlushReason>([
  'visibility-hidden',
  'pagehide',
  'beforeunload',
]);

export interface DistributedLogError {
  name?: string;
  message?: string;
  stack?: string;
}

/** A single structured log record that carries its own linkage identifiers. */
export interface DistributedLogEvent {
  id: string;
  ts: number;
  level: LogLevel;
  message: string;
  source: string;
  traceId: string;
  correlationId?: string;
  deviceId?: string;
  context?: Record<string, unknown>;
  error?: DistributedLogError;
}

export interface LogEventOptions {
  correlationId?: string;
  context?: Record<string, unknown>;
  error?: unknown;
  source?: string;
}

/** Result of closing out an HTTP operation for the interceptor. */
export interface HttpLogInput {
  method: string;
  url: string;
  correlationId: string;
  status: number;
  ok: boolean;
  ms?: number;
  error?: unknown;
}

export interface DistributedLogConfig {
  /** Master switch. When false nothing is buffered or shipped. Default: true. */
  enabled: boolean;
  /**
   * Absolute or same-origin relative URL for the log sink.
   * Empty (the default) disables the transport but keeps local linkage active.
   */
  endpoint: string;
  /** Max buffered events before the oldest are dropped. Default: 500. */
  bufferLimit: number;
  /** Idle-flush interval in ms. `0` disables the periodic timer. Default: 30000. */
  flushIntervalMs: number;
  /** Levels mirrored to the console for local linkage. Default: warn/error/fatal. */
  consoleLevels: LogLevel[];
  /** URL substring identifying our own sink requests (skipped from logging). */
  selfPath: string;
}

/**
 * Optional DI override for the distributed log configuration.
 * Consumed by `app.module` to read `environment.distributedLog` without the
 * service itself importing the environment (keeps the service unit-testable).
 */
export const DISTRIBUTED_LOG_CONFIG = new InjectionToken<
  Partial<DistributedLogConfig>
>('DISTRIBUTED_LOG_CONFIG');

const DEFAULT_CONFIG: DistributedLogConfig = {
  enabled: true,
  endpoint: '',
  bufferLimit: 500,
  flushIntervalMs: 30000,
  consoleLevels: ['warn', 'error', 'fatal'],
  selfPath: '/telemetry/logs',
};

const SESSION_TRACE_KEY = 'webddn.dl.traceId';
const DEVICE_ID_KEY = 'webddn.dl.deviceId';

/**
 * Generates a linkage identifier. Prefers a cryptographic UUID, degrades to a
 * random-hex id, and finally to a timestamped fallback for non-browser hosts
 * (e.g. server-side rendering / jsdom without `crypto.randomUUID`).
 */
function newId(): string {
  const crypto = (globalThis as { crypto?: Crypto }).crypto;
  if (crypto?.randomUUID) {
    try {
      return crypto.randomUUID();
    } catch {
      /* fall through to getRandomValues */
    }
  }
  if (crypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    let out = '';
    for (let i = 0; i < bytes.length; i++) {
      out += bytes[i].toString(16).padStart(2, '0');
    }
    return out;
  }
  return `id-${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

function readStorage(storage: Storage | undefined, key: string): string {
  try {
    return storage?.getItem(key) ?? '';
  } catch {
    return '';
  }
}

function writeStorage(storage: Storage | undefined, key: string, value: string): void {
  try {
    storage?.setItem(key, value);
  } catch {
    /* quota / private-mode / SSR — ignore, linkage just won't persist */
  }
}

/**
 * Central hub for distributed-logging *linkage* and *graceful signal
 * interception* on the client.
 *
 * Linkage: every event carries a session-scoped `traceId` and (optionally) a
 * per-operation `correlationId`. The interceptor attaches these as request
 * headers and the socket service propagates `traceId` on its handshake, so a
 * single id ties together client logs, HTTP logs and server logs.
 *
 * Graceful interception: `installGracefulSignals()` listens to `pagehide`,
 * `visibilitychange` and `beforeunload`, flushing the buffered queue via
 * `navigator.sendBeacon` (synchronous, survives unload) so no telemetry is lost
 * when the tab closes, refreshes or is backgrounded.
 */
@Injectable({
  providedIn: 'root',
})
export class DistributedLogService {
  private readonly config: DistributedLogConfig;
  private readonly buffer: DistributedLogEvent[] = [];
  private readonly shutdownListeners: Array<() => void> = [];
  private readonly onVisibility = (): void => {
    if (document.visibilityState === 'hidden') {
      this.flush('visibility-hidden');
    }
  };
  private readonly onPageHide = (): void => {
    this.flush('pagehide');
  };
  private readonly onBeforeUnload = (): void => {
    this.flush('beforeunload');
  };

  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private gracefulInstalled = false;
  private deviceIdCache: string | null = null;

  /** Session-scoped trace id: survives reloads, fresh per tab. */
  private readonly traceId: string;

  /**
   * Reactive view of the active trace context. Handy for a debug overlay or for
   * components that want to surface the current `traceId` in the UI.
   */
  readonly context = signal<{ traceId: string; deviceId?: string }>({
    traceId: this.resolveTraceId(),
  });

  constructor() {
    // Angular 22: use inject() instead of @Optional() @Inject() constructor decorators
    const provided = inject(DISTRIBUTED_LOG_CONFIG, { optional: true });
    this.config = { ...DEFAULT_CONFIG, ...(provided ?? {}) };
    this.traceId = this.context().traceId;

    // Angular 22: use DestroyRef for automatic cleanup instead of manual dispose
    inject(DestroyRef).onDestroy(() => {
      this.disposeGracefulSignals();
      if (this.idleTimer !== null) {
        clearTimeout(this.idleTimer);
        this.idleTimer = null;
      }
    });
  }

  get enabled(): boolean {
    return this.config.enabled;
  }

  get traceIdValue(): string {
    return this.traceId;
  }

  /** Device-scoped id persisted in `localStorage` (survives sessions). */
  get deviceId(): string {
    if (!this.deviceIdCache) {
      this.deviceIdCache = this.resolveDeviceId();
    }
    return this.deviceIdCache;
  }

  get pendingCount(): number {
    return this.buffer.length;
  }

  get isGracefulInstalled(): boolean {
    return this.gracefulInstalled;
  }

  /** Mint a fresh correlation id for a single logical operation. */
  newCorrelationId(): string {
    return newId();
  }

  /** True when `url` targets our own sink, so we don't log our own shipping. */
  isSinkRequest(url: string): boolean {
    return this.config.selfPath.length > 0 && url.includes(this.config.selfPath);
  }

  log(level: LogLevel, message: string, options?: LogEventOptions): void {
    if (!this.config.enabled) {
      return;
    }
    const event: DistributedLogEvent = {
      id: newId(),
      ts: Date.now(),
      level,
      message,
      source: options?.source ?? 'app',
      traceId: this.traceId,
      correlationId: options?.correlationId,
      deviceId: this.deviceId,
      context: options?.context,
      error: toLogError(options?.error),
    };
    this.push(event);
    this.mirrorToConsole(level, event);
  }

  info(message: string, options?: LogEventOptions): void {
    this.log('info', message, options);
  }

  warn(message: string, options?: LogEventOptions): void {
    this.log('warn', message, options);
  }

  error(message: string, options?: LogEventOptions): void {
    this.log('error', message, options);
  }

  debug(message: string, options?: LogEventOptions): void {
    this.log('debug', message, options);
  }

  fatal(message: string, options?: LogEventOptions): void {
    this.log('fatal', message, options);
  }

  /** Record the outcome of an HTTP operation (used by the interceptor). */
  recordHttp(input: HttpLogInput): void {
    this.log(input.ok ? 'debug' : 'warn', `http ${input.status} ${input.method}`, {
      correlationId: input.correlationId,
      source: 'http',
      context: {
        method: input.method,
        url: input.url,
        status: input.status,
        ok: input.ok,
        ms: input.ms,
      },
      error: input.ok ? undefined : input.error,
    });
  }

  /**
   * Drain the buffer toward the sink.
   *
   * Signal-flush reasons (`pagehide` / `beforeunload` / `visibility-hidden`) use
   * `navigator.sendBeacon` so the request survives unload; if the beacon can't be
   * queued the events are retained for a later flush. Non-signal flushes use an
   * async keepalive request (fire-and-forget).
   *
   * @returns the number of events shipped (0 if disabled, no endpoint, or empty).
   */
  flush(reason: FlushReason = 'manual'): number {
    if (!this.config.enabled) {
      return 0;
    }
    const isSignal = SIGNAL_FLUSH_REASONS.has(reason);
    if (this.buffer.length === 0) {
      if (isSignal) {
        this.emitShutdown();
      }
      return 0;
    }
    if (this.config.endpoint.length === 0) {
      if (isSignal) {
        this.emitShutdown();
      }
      return 0;
    }

    const events = this.buffer.slice();
    const payload = JSON.stringify({
      sentAt: Date.now(),
      reason,
      traceId: this.traceId,
      deviceId: this.deviceId,
      count: events.length,
      events,
    });

    let shipped = false;
    if (isSignal) {
      shipped = this.shipBeacon(payload);
    } else {
      this.shipAsync(payload);
      shipped = true;
    }

    if (shipped) {
      this.buffer.length = 0;
    }
    if (isSignal) {
      this.emitShutdown();
    }
    return shipped ? events.length : 0;
  }

  /**
   * Register a callback fired when a graceful teardown flush happens. Returns an
   * unsubscribe function. Used to link the distributed channel (e.g. a socket
   * "client-gone" event) to the same teardown moment.
   */
  onShutdown(handler: () => void): () => void {
    this.shutdownListeners.push(handler);
    return () => {
      const i = this.shutdownListeners.indexOf(handler);
      if (i >= 0) {
        this.shutdownListeners.splice(i, 1);
      }
    };
  }

  /**
   * Attach teardown listeners so buffered logs are flushed when the page is
   * hidden, closed or reloaded. Idempotent; safe to call multiple times.
   */
  installGracefulSignals(onShutdown?: () => void): void {
    const g = globalThis;
    if (typeof (g as { addEventListener?: unknown }).addEventListener !== 'function') {
      return;
    }
    if (onShutdown) {
      this.onShutdown(onShutdown);
    }
    if (this.gracefulInstalled) {
      return;
    }
    const w = g as unknown as Window;
    w.addEventListener('visibilitychange', this.onVisibility);
    w.addEventListener('pagehide', this.onPageHide);
    w.addEventListener('beforeunload', this.onBeforeUnload);
    this.gracefulInstalled = true;
  }

  /** Remove teardown listeners. Primarily for tests. */
  disposeGracefulSignals(): void {
    if (!this.gracefulInstalled) {
      return;
    }
    const w = globalThis as unknown as Window;
    w.removeEventListener?.('visibilitychange', this.onVisibility);
    w.removeEventListener?.('pagehide', this.onPageHide);
    w.removeEventListener?.('beforeunload', this.onBeforeUnload);
    this.gracefulInstalled = false;
  }

  /** Test hook: clear the buffer, open state and any pending timer. */
  resetForTest(): void {
    this.buffer.length = 0;
    this.shutdownListeners.length = 0;
    this.deviceIdCache = null;
    this.disposeGracefulSignals();
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  private resolveTraceId(): string {
    const session =
      typeof sessionStorage !== 'undefined' ? sessionStorage : undefined;
    const existing = readStorage(session, SESSION_TRACE_KEY);
    if (existing) {
      return existing;
    }
    const id = newId();
    writeStorage(session, SESSION_TRACE_KEY, id);
    return id;
  }

  private resolveDeviceId(): string {
    const local =
      typeof localStorage !== 'undefined' ? localStorage : undefined;
    const existing = readStorage(local, DEVICE_ID_KEY);
    if (existing) {
      return existing;
    }
    const id = newId();
    writeStorage(local, DEVICE_ID_KEY, id);
    return id;
  }

  private push(event: DistributedLogEvent): void {
    this.buffer.push(event);
    if (this.buffer.length > this.config.bufferLimit) {
      this.buffer.splice(0, this.buffer.length - this.config.bufferLimit);
    }
    this.scheduleIdleFlush();
  }

  private scheduleIdleFlush(): void {
    if (
      this.config.flushIntervalMs <= 0 ||
      this.idleTimer !== null ||
      this.buffer.length === 0
    ) {
      return;
    }
    this.idleTimer = setTimeout(() => {
      this.idleTimer = null;
      this.flush('idle');
    }, this.config.flushIntervalMs);
  }

  private shipBeacon(payload: string): boolean {
    const nav = (globalThis as { navigator?: Navigator }).navigator;
    if (!nav?.sendBeacon) {
      return false;
    }
    try {
      return nav.sendBeacon(
        this.config.endpoint,
        new Blob([payload], { type: 'application/json' })
      );
    } catch {
      return false;
    }
  }

  private shipAsync(payload: string): void {
    if (typeof fetch === 'function') {
      try {
        void fetch(this.config.endpoint, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: payload,
          keepalive: true,
          credentials: 'omit',
        }).catch(() => {
          /* telemetry must never throw */
        });
        return;
      } catch {
        /* fall through to XHR */
      }
    }
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', this.config.endpoint, true);
      xhr.setRequestHeader('content-type', 'application/json');
      xhr.send(payload);
    } catch {
      /* telemetry must never throw */
    }
  }

  private mirrorToConsole(level: LogLevel, event: DistributedLogEvent): void {
    if (!this.config.consoleLevels.includes(level)) {
      return;
    }
    const tag = `[dl ${event.traceId}${event.correlationId ? ' ' + event.correlationId : ''}]`;
    const fn =
      level === 'debug'
        ? console.debug
        : level === 'info'
        ? console.info
        : level === 'warn'
        ? console.warn
        : console.error;
    try {
      fn.call(console, tag, event.message, event.context ?? event.error ?? '');
    } catch {
      /* ignore console overrides */
    }
  }

  private emitShutdown(): void {
    const listeners = this.shutdownListeners.slice();
    for (const handler of listeners) {
      try {
        handler();
      } catch {
        /* a shutdown hook must never break the teardown */
      }
    }
  }
}

function toLogError(err: unknown): DistributedLogError | undefined {
  if (!err) {
    return undefined;
  }
  if (err instanceof Error) {
    return { name: err.name, message: err.message, stack: err.stack };
  }
  if (typeof err === 'string') {
    return { message: err };
  }
  if (typeof err === 'object') {
    const record = err as Record<string, unknown>;
    return {
      name: typeof record['name'] === 'string' ? (record['name'] as string) : undefined,
      message: typeof record['message'] === 'string' ? (record['message'] as string) : undefined,
      stack: typeof record['stack'] === 'string' ? (record['stack'] as string) : undefined,
    };
  }
  return undefined;
}
